import { CircularProgress } from "@material-ui/core";
import Button from "@material-ui/core/Button";
import CssBaseline from "@material-ui/core/CssBaseline";
import Grid from "@material-ui/core/Grid";
import Link from "@material-ui/core/Link";
import Snackbar from "@material-ui/core/Snackbar";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import Alert from "@material-ui/lab/Alert";
import clsx from "clsx";
import { type ChangeEvent, type FormEvent, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router";
import { TestimonyCard } from "../../../common/testimonyCard";
import { LinkKeepUTM } from "../../../shared/components/LinkKeepUTM";
import { InactiveSubscriptionDialog } from "../../../shared/components/inactiveSubscription";
import { Footer } from "../../../shared/components/participantLayout";
import { useHistory } from "../../../shared/hooks/useHistory";
import { refreshToken as refreshTokenFnc } from "../../../shared/utils/refreshToken";
import { validateEmail } from "../../../shared/validateEmail";
import type { RootState } from "../../../store";
import { login, setIsFetchingLogin, setIsLoginError } from "../../actions";
import { openSubscriptionModal } from "../../actions/login.actions";
import { useStyles } from "./loginPage.styles";

const dataCard = {
  image: "/static/images/avatar/1.jpg",
  cardText: `I love the Fruition program! Enrolling helped me put a plan in place and stay on track to hitting my goals.
  Now, I am less stressed and one step closer to being financially well.`,
  clientName: "Liz Parker",
  position: "",
};

export interface LoginLocationState {
  isSuccesUpdatePrimaryEmail?: boolean;
  isCreatePasswordSuccess?: boolean;
  isResetPasswordSuccess?: boolean;
  isResetPasswordExpired?: boolean;
  isTokenUpdateEmailExpired?: boolean;
}

export const LoginPage = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const { state } = useLocation<LoginLocationState>();

  const [formValues, setFormValues] = useState({ username: "", password: "" });
  const [usernameError, setUsernameError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [isOpenSuccessUpdatePrimaryEmail, setIsOpenSuccessUpdatePrimaryEmail] =
    useState(state && state?.isSuccesUpdatePrimaryEmail);

  const [isSubscriptionModalOpen, setIsSubsccriptionModalOpen] =
    useState(false);

  const {
    isSubscriptionInactive,
    participantProfile,
    login: { isFetchingLogin, isLoginError, loginErrorMessage },
    hasProfile,
  } = useSelector((state: RootState) => {
    return {
      isSubscriptionInactive: state.auth.login.isSubscriptionInactive,
      participantProfile: state.auth.login.stripeParticipant,
      login: state.auth.login,
      hasProfile: !!state.profile.participantProfileId,
    };
  });

  if (state?.isCreatePasswordSuccess) {
    refreshTokenFnc();
  }

  useEffect(() => {
    setIsSubsccriptionModalOpen(isSubscriptionInactive);
  }, [isSubscriptionInactive]);

  const isValidLogin = () => {
    const { username, password } = formValues;
    clearErrors();

    if (username.length && !validateEmail(username)) {
      setUsernameError("Invalid Email");
      return false;
    }

    if (!username.length) {
      setUsernameError("Email is required");
      return false;
    }

    if (!password.length) {
      setPasswordError("Password Required");
      return false;
    }

    return true;
  };

  useEffect(() => {
    dispatch(setIsLoginError({ isError: false }));
    dispatch(setIsFetchingLogin({ isFetching: false }));
  }, [dispatch]);

  useEffect(() => {
    if (!hasProfile) return;

    const referrer = location.state?.from?.pathname || "/";
    history.replace(referrer === "/" ? "/participant/dashboard" : referrer);
  }, [history, dispatch, hasProfile, location]);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!isValidLogin()) return;

    dispatch(
      login({
        username: formValues.username,
        password: formValues.password,
      })
    );
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormValues({ ...formValues, [name]: value });
  };

  const closeSubscriptionModal = () => {
    setIsSubsccriptionModalOpen(false);
    dispatch(openSubscriptionModal(false));
  };

  const clearErrors = () => {
    setUsernameError("");
    setPasswordError("");
  };

  const handleClose = () => {
    dispatch(setIsLoginError({ isError: false }));
    openSubscriptionModal(false);
  };

  return (
    <div className={classes.loginContent}>
      <CssBaseline />
      <>
        <div className={classes.wrapperLogin}>
          <div className={clsx(classes.paper, classes.loginLeftContent)}>
            <div className={classes.formWrapper}>
              {state && state.isResetPasswordSuccess && (
                <Typography className={classes.errorMessage}>
                  The password has been reset
                </Typography>
              )}

              {state && state.isResetPasswordExpired && (
                <Typography className={classes.errorMessage}>
                  The reset password link has expired, please go to reset
                  password page and request again
                </Typography>
              )}

              {state && state.isTokenUpdateEmailExpired && (
                <Typography className={classes.errorMessage}>
                  The update primary email link has expired, please go to
                  profile page and request again
                </Typography>
              )}

              <img
                className={classes.logo}
                src="/logo-fruition.webp"
                alt="Logo Fruition"
              />
              <form className={classes.form} noValidate onSubmit={handleSubmit}>
                <TextField
                  error={!!usernameError}
                  helperText={usernameError}
                  variant="outlined"
                  margin="normal"
                  required
                  fullWidth
                  id="email"
                  label="Email Address"
                  name="username"
                  autoComplete="email"
                  autoFocus
                  value={formValues.username}
                  onChange={handleChange}
                />
                <TextField
                  error={!!passwordError}
                  helperText={passwordError}
                  variant="outlined"
                  margin="normal"
                  required
                  fullWidth
                  name="password"
                  label="Password"
                  type="password"
                  id="password"
                  autoComplete="current-password"
                  onChange={handleChange}
                />
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  color="primary"
                  className={classes.submitButton}
                  disabled={isFetchingLogin}
                >
                  {isFetchingLogin ? <CircularProgress size={18} /> : "Sign In"}
                </Button>
                <Grid className={classes.supportContainer} container>
                  <Typography
                    variant="caption"
                    className={classes.supportInfoLabel}
                  >
                    If you have any questions, please contact support at{" "}
                    <a
                      href="mailto:helpdesk@meetfruition.com"
                      className={classes.supportHighlightText}
                    >
                      helpdesk@meetfruition.com
                    </a>
                  </Typography>
                </Grid>
                <Grid
                  className={classes.actionsFooter}
                  container
                  justifyContent="space-between"
                  alignItems="stretch"
                  alignContent="center"
                >
                  <Grid item xs className={classes.forgotLink}>
                    <Link to="/recover" variant="body2" component={LinkKeepUTM}>
                      Forgot password?
                    </Link>
                  </Grid>
                  <Grid item xs className={classes.resendWelcomeEmailLink}>
                    <Link
                      to="/send-welcome"
                      variant="body2"
                      component={LinkKeepUTM}
                    >
                      Send Welcome Email
                    </Link>
                  </Grid>
                </Grid>
              </form>
            </div>
          </div>
          <div className={classes.loginRightContent}>
            <div className={classes.testimonialsContainer}>
              <Typography
                component="h1"
                variant="h5"
                className={classes.welcomeTitle}
              >
                What Our Clients Say
              </Typography>
              <div className={classes.welcomeSeparator} />
              <br />
              <br />
              <TestimonyCard cardInfo={dataCard} />
            </div>
          </div>
        </div>
        <Footer />
      </>
      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        open={isOpenSuccessUpdatePrimaryEmail}
        autoHideDuration={6000}
        onClose={() => setIsOpenSuccessUpdatePrimaryEmail(false)}
      >
        <Alert severity="success">
          Your primary email was updated successfully
        </Alert>
      </Snackbar>
      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        open={isLoginError}
        autoHideDuration={6000}
        onClose={handleClose}
      >
        <Alert onClose={handleClose} severity="warning">
          {loginErrorMessage}
        </Alert>
      </Snackbar>
      <InactiveSubscriptionDialog
        open={isSubscriptionModalOpen}
        onClose={() => closeSubscriptionModal()}
        participantProfileId={participantProfile?.profileId}
        subscriptionStatus={participantProfile?.subscriptionStatus}
      />
    </div>
  );
};
